import React from 'react';

import { useIntl } from 'react-intl';

import { useGetGatewayPass } from 'src/features/gatewayPass/api/useGetGatewayPass';
import ZoneTranslation from 'src/features/gatewayPass/components/ZoneTranslation';
import ImageCarousel from 'src/features/visitor/components/carousel/ImageCarousel';
import { visitorTypeBadgeOptions } from 'src/features/gatewayPass/utils/constants';
import { timestampToId } from 'src/features/visitor/utils/timestampToId';
import { gatewayMessages } from 'src/features/gatewayPass/utils/messages';
import { GeneralBlockHeader, GeneralBlockTitle } from 'src/registry/components/blocks/GeneralBlock';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { Translation } from 'src/types';
import { useParking } from 'src/providers/ParkingProvider';
import { PATH } from 'src/features/navigation/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';

type GatewayPassSidePanelProps = {
    gatewayPassId?: string;
};

const GatewayPassSidePanel = ({ gatewayPassId }: GatewayPassSidePanelProps) => {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: gatewayPass } = useGetGatewayPass({
        gatewayPassId: gatewayPassId ?? '',
        parkingId,
        config: {
            enabled: !!gatewayPassId,
        },
    });

    const permissionAccessVisitor = useHasPermissions([PERMISSION.ACCESS_VISITORS]);
    const permissionAccessActiveStay = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const permissionAccessPastStays = useHasPermissions([PERMISSION.ACCESS_PAST_STAYS]);

    const translate = (translations: Translation[]) =>
        translations.find((translation) => translation.language === locale);

    const visitor = React.useMemo(() => {
        const visitor = gatewayPass?.visitor;
        if (!gatewayPass || !visitor) return '-';
        return `${visitor.main_identifier.value} (${formatMessage(visitorTypeBadgeOptions[visitor.type].label).toLowerCase()}) ${visitor.owner ? `- ${visitor.owner.name}` : ''}`;
    }, [formatMessage, gatewayPass]);

    const parkingStayRef = React.useMemo(() => {
        if (!gatewayPass) return '-';
        const activeStayRef = gatewayPass?.active_stay?.ref;
        const pastStayRef = gatewayPass?.past_stay?.ref;

        return activeStayRef ? activeStayRef : (pastStayRef ?? '-');
    }, [gatewayPass]);

    const parkingStayRerouteUrl = React.useMemo(() => {
        if (!gatewayPass) return null;
        const activeStayId = gatewayPass?.active_stay?.id;
        const pastStayId = gatewayPass?.past_stay?.id;

        const urlPath =
            permissionAccessActiveStay && activeStayId
                ? `/${PATH.VISITORS}/active-stays/dashboard/${activeStayId}`
                : permissionAccessPastStays && pastStayId
                  ? `/${PATH.VISITORS}/past-stays/dashboard/${pastStayId}`
                  : null;

        return urlPath;
    }, [gatewayPass, permissionAccessActiveStay, permissionAccessPastStays]);

    return (
        <>
            {gatewayPass ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">
                            {formatMessage(gatewayMessages.gatewayPass)}
                        </DialogTitle>
                        <ZoneTranslation translations={gatewayPass.entry_zone?.translations || []} isEntry />
                    </div>
                    <DialogDescription>{timestampToId(gatewayPass?.time)}</DialogDescription>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}
            <DialogSeparator />
            <DialogBody>
                <ImageCarousel
                    imageSlides={[
                        {
                            image: gatewayPass?.photo || null,
                            title: translate(gatewayPass?.gateway?.translations ?? [])?.name,
                            subtitle: `${formatMessage(gatewayMessages.pass)} ${timestampToId(gatewayPass?.time)}`,
                        },
                    ]}
                />
                <GeneralBlockAttribute
                    label={formatMessage(gatewayMessages.visitor)}
                    value={visitor}
                    link={
                        permissionAccessVisitor && gatewayPass?.visitor?.id
                            ? `/${PATH.VISITORS}/visitors/${gatewayPass?.visitor?.id}`
                            : undefined
                    }
                    icon={ICON_MAP.ARROW_UP_RIGHT}
                />
                <GeneralBlockAttribute
                    label={formatMessage(gatewayMessages.parkingStay)}
                    value={parkingStayRef}
                    link={parkingStayRerouteUrl ?? undefined}
                    icon={ICON_MAP.ARROW_UP_RIGHT}
                />
                <GeneralBlockAttribute
                    label={formatMessage(gatewayMessages.identifiers)}
                    value={gatewayPass?.identifiers?.map((identifier) => identifier.value).join(', ')}
                />
                <GeneralBlockAttribute
                    label={formatMessage(gatewayMessages.gateway)}
                    value={translate(gatewayPass?.gateway?.translations ?? [])?.name}
                />
                <GeneralBlockAttribute
                    label={formatMessage(gatewayMessages.passedOn)}
                    value={getFormattedDateTimeForDetailPanel(gatewayPass?.time)}
                />
                <div className="flex flex-col">
                    <GeneralBlockHeader variant="dashboard">
                        <GeneralBlockTitle>{formatMessage(gatewayMessages.entryZone)}</GeneralBlockTitle>
                    </GeneralBlockHeader>
                    <ZoneTranslation translations={gatewayPass?.entry_zone?.translations || []} isEntry />
                </div>
                <div className="flex flex-col">
                    <GeneralBlockHeader variant="dashboard">
                        <GeneralBlockTitle>{formatMessage(gatewayMessages.exitZone)}</GeneralBlockTitle>
                    </GeneralBlockHeader>
                    <ZoneTranslation translations={gatewayPass?.exit_zone?.translations || []} />
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
};

export default GatewayPassSidePanel;
